export interface IAndroidApp {
  postMessage: (message: string) => void;
}

declare global {
  export interface Window {
    androidApp?: IAndroidApp;
    webkit?: {
      messageHandlers?: {
        appInterface?: {
          postMessage: typeof window.postMessage;
        };
      };
    };
  }
}

// Events (fire-and-forget, no response expected)
export const webToNativeActionEventNames = {
  testAction: 'trigger_random_event_for_test',
  startSoftwareInstallation: 'VPC_start_software_installation',
  showAcknowledgements: 'VPC_show_acknowledgements',
  sessionExpired: 'VPC_session_expired',
  sessionActive: 'VPC_session_active',
  logout: 'VPC_logout',
  editProfile: 'VPC_edit_profile',
};
export type TWebToNativeEvent = keyof typeof webToNativeActionEventNames;

// Requests (expect a response)
export const webToNativeRequestNames = {
  handshake: 'VPC_external_application_handshake_init',
  refreshToken: 'VPC_refresh_token',
};
export type TWebToNativeRequest = keyof typeof webToNativeRequestNames;

// Union of all web-to-native actions (for backwards compatibility)
export type TNativeAction = TWebToNativeEvent | TWebToNativeRequest;

// Incoming requests/events from native
export const nativeToWebActionNames = {
  getInstallationStatus: 'VPC_get_installation_status',
  openCookieSettings: 'VPC_show_cookie_preferences',
  provideAuthTokens: 'VPC_provide_auth_tokens',
};
export type TNativeToWebAction = keyof typeof nativeToWebActionNames;

// Message type constants for type safety
export const MessageTypes = {
  WEB_TO_NATIVE_REQUEST: 'webToNativeRequest',
  WEB_TO_NATIVE_RESPONSE: 'webToNativeResponse',
  WEB_TO_NATIVE_EVENT: 'webToNativeEvent',
  NATIVE_TO_WEB_REQUEST: 'nativeToWebRequest',
  NATIVE_TO_WEB_RESPONSE: 'nativeToWebResponse',
  NATIVE_TO_WEB_EVENT: 'nativeToWebEvent',
} as const;

export type MessageType = (typeof MessageTypes)[keyof typeof MessageTypes];

// User information passed to native applications
// All fields use null for missing values to ensure proper JSON serialization
// (undefined values are stripped by JSON.stringify, resulting in empty objects)
export interface UserInfo {
  endCustomerId: string | null; // Customer ID from Azure B2C token (null when not authenticated)
  b2cId: string | null; // Azure B2C user ID (UUID format) - null when not authenticated
  email: string | null; // User's email address - null when not authenticated
}

// Enhanced message types for bidirectional communication
export interface BaseMessage {
  name: string;
  requestId: string;
  payload?: unknown;
  channel: 'ios' | 'android';
  applicationId?: string;
}

// Web → Native Messages
export interface WebToNativeRequest extends BaseMessage {
  type: typeof MessageTypes.WEB_TO_NATIVE_REQUEST;
  userInfo: UserInfo;
}

export interface WebToNativeResponse extends BaseMessage {
  type: typeof MessageTypes.WEB_TO_NATIVE_RESPONSE;
  userInfo: UserInfo;
  responseToRequestId: string;
  success: boolean;
  error?: string;
}

export interface WebToNativeEvent extends BaseMessage {
  type: typeof MessageTypes.WEB_TO_NATIVE_EVENT;
  userInfo: UserInfo;
}

// Native → Web Messages
export interface NativeToWebRequest extends BaseMessage {
  type: typeof MessageTypes.NATIVE_TO_WEB_REQUEST;
}

export interface NativeToWebResponse extends BaseMessage {
  type: typeof MessageTypes.NATIVE_TO_WEB_RESPONSE;
  responseToRequestId: string;
  success: boolean;
  error?: string;
}

export interface NativeToWebEvent extends BaseMessage {
  type: typeof MessageTypes.NATIVE_TO_WEB_EVENT;
}

export type NativeMessage =
  | WebToNativeRequest
  | WebToNativeResponse
  | WebToNativeEvent
  | NativeToWebRequest
  | NativeToWebResponse
  | NativeToWebEvent;

// Callback types
export interface PendingRequest {
  action: TNativeAction;
  onSuccess: (event: MessageEvent) => void;
  onError: (err: string) => void;
  timestamp: number;
}

export interface RequestHandler {
  handler: (payload: unknown, requestId: string) => Promise<unknown> | unknown;
}

export interface LegacyOptions {
  onSuccess: (event: MessageEvent) => void;
  onTrigger?: (message: string) => void;
  onResponse?: (message: string) => void;
  onError: (err: string) => void;
  onCancelAction?: () => void;
}

export interface BidirectionalOptions {
  onTrigger?: (message: string) => void;
  onResponse?: (message: string) => void;
  onCancelAction?: () => void;
  requestTimeout?: number; // in milliseconds, default 30000
}
